<template>
  <div
    v-if="!storeKey"
    v-bind="$attrs"
    class="document-upload-container relative"
  >
    <div
      v-if="annexure?.id && annexure?.document"
      class="document-list-item"
    >
      <a
        :href="annexure.document.url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FontAwesomeIcon :icon="faFilePdf" />
        {{ isAnnexureContext ? annexure.document.name : annexure.name }}
      </a>

      <DocumentActions
        class="ml-auto"
        subject="document"
        @edit-clicked="openFormModal(annexure)"
        @delete-clicked="openDeleteModal(annexure)"
      />
    </div>

    <AppButton
      v-else
      :disabled="disabled"
      class="upload-button btn-dashed btn-outline btn-secondary"
      @click="openFormModal"
    >
      <FontAwesomeIcon :icon="faPlus" />
      Upload document
    </AppButton>
  </div>

  <div
    v-else
    class="w-full"
  >
    <!-- List Existing Documents -->
    <div
      v-if="attachedDocuments.length > 0"
      class="document-upload-container"
    >
      <div
        v-for="document in attachedDocuments"
        :key="document.id"
        class="document-list-item bg-gray-50 border rounded-md px-2 mb-3 text-[13.5px]"
      >
        <a
          :href="document.document?.url"
          target="_blank"
          rel="noopener noreferrer"
          class="!text-primary !hover:text-secondary"
        >
          <FontAwesomeIcon :icon="faFilePdf" />
          {{ document.name || document.document?.name || 'Unnamed Document' }}
        </a>

        <DocumentActions
          subject="document"
          @edit-clicked="openFormModal(document)"
          @delete-clicked="openDeleteModal(document)"
        />
      </div>
    </div>

    <!-- Upload Button -->
    <AppButton
      :disabled="formModal.processing"
      class="btn-dashed btn-outline btn-secondary text-sm py-1 mb-5"
      @click="openFormModal(null)"
    >
      <FontAwesomeIcon :icon="faPlus" />
      Attach Document
    </AppButton>
  </div>

  <AppModal
    v-if="formModal.type === 'document'"
    :title="formModal.isEditing ? 'Edit document' : 'Upload a document'"
    :disabled="formModal.processing"
    class="offset-top"
    @close="handleCancel"
  >
    <template #body>
      <AnnexureForm
        v-model="formModal.data"
        :is-editing="formModal.isEditing"
        :errors="errors"
        @submit="saveDocument"
      />
    </template>

    <template #footer>
      <ButtonSubmitRow
        :processing="formModal.processing"
        @submit-clicked="saveDocument"
        @cancel-clicked="handleCancel"
      />
    </template>
  </AppModal>

  <ConfirmationModal
    v-if="deleteModal.type === 'document'"
    intent="danger"
    title="Delete document"
    :processing="deleteModal.processing"
    @close="deleteModal.close"
    @confirm="deleteDocument"
  >
    <span class="block">
      Are you sure you want to delete the "<strong>{{ deleteModal.data.name }}</strong>" pdf?
    </span>
    This action cannot be undone.
  </ConfirmationModal>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { AppButton, AppModal } from '@/components/app';
import { ButtonSubmitRow } from '@/components/buttons';
import { ConfirmationModal } from '@/components/modals';
import { DocumentActions } from '@/components/plan-document/components';
import { AnnexureForm } from '@/components/plan-document/forms';

import { faFilePdf, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useModal, useToasts, useHttp } from '@/composables';
import { usePlanStore } from '@/store';

defineOptions({ inheritAttrs: false });

const planStore = usePlanStore();
const route = useRoute();
const toasts = useToasts();
const formModal = useModal();
const deleteModal = useModal();

const emit = defineEmits(['saved', 'deleted']);

const props = defineProps({
  annexure: { type: Object, default: null },
  disabled: Boolean,
  currentSection: { type: String, default: null },
  storeKey: { type: String, default: null },
});

const errors = ref({
  name: null,
  type: null,
  document: null,
});

const isAnnexureContext = computed(() => {
  return props.currentSection === 'required-annexures' || props.currentSection === 'additional-annexures';
});

// Helper to resolve nested path in planStore.content as it is in dot-notation (e.g., 'situationalAnalysis.overview.documents')
const getNestedProperty = (obj, path) => {
  return path.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
};

// Gets the array of Annexure IDs from the store using the storeKey
const documentIds = computed(() => {
  if (!props.storeKey) return [];
  const ids = getNestedProperty(planStore.content, props.storeKey);
  return Array.isArray(ids) ? ids : [];
});

// Maps the array of IDs (from documentIds) to actual Annexure objects using the store's lookup
const attachedDocuments = computed(() => {
  return documentIds.value
    .map(id => planStore.annexureLookup[id])
    .filter(Boolean);
});

const openFormModal = (docOrEvent) => {
  // Determine if the argument is a DOM Event (click) or a Document Object
  const isEvent = docOrEvent && (docOrEvent instanceof Event || !!docOrEvent.target);

  // Use the passed document object if it's not an event, otherwise fallback to props.annexure
  const target = (!isEvent && docOrEvent) ? docOrEvent : props.annexure;

  const isEditing = !!(target && target.id);
  // Only set the default type to the additional option when on the Additional Annexure page
  const additionalAnnexureType = (props.currentSection === 'additional-annexures') ? planStore.annexureTypes.find(annexureType => !annexureType.required) : null;

  formModal.open('document', {
    id: isEditing ? target.id : null,
    // If target has a type object with id, use it
    type: target?.type?.id ?? (target?.annexureType ?? additionalAnnexureType?.id),
    name: target?.name || '',
    file: target?.document || null,
  }, { isEditing: !!isEditing });
};

const openDeleteModal = (document) => {
  deleteModal.open('document', document);
};

// Remove ID from the specific content section array
const removeDocumentIdFromContent = (idToRemove) => {
  if (!props.storeKey) return;

  const pathParts = props.storeKey.split('.');
  const targetKey = pathParts.pop();
  const targetObj = pathParts.reduce((o, i) => o[i], planStore.content);

  if (targetObj && Array.isArray(targetObj[targetKey])) {
    targetObj[targetKey] = targetObj[targetKey].filter(id => id !== idToRemove);
  }
};

/**
 * Recursive function to traverse planStore.content and remove a specific ID
 * from any array found in the object tree. This handles the case where the backend
 * deleted an old annexure, so we must remove references to it from the frontend store.
 */
const cleanupGlobalContentReferences = (idToRemove) => {
  if (!planStore.content) return;

  const traverseAndRemove = (content) => {
    for (const key in content) {
      if (Object.prototype.hasOwnProperty.call(content, key)) {
        if (Array.isArray(content[key])) {
          // If array contains the ID, filter it out
          const idx = content[key].indexOf(idToRemove);
          if (idx !== -1) {
            content[key] = content[key].filter(val => val !== idToRemove);
          }

          // Recursively check objects inside arrays
          content[key].forEach(item => {
            if (typeof item === 'object' && item !== null) {
              traverseAndRemove(item);
            }
          });
        } else if (typeof content[key] === 'object' && content[key] !== null) {
          // Recurse into objects
          traverseAndRemove(content[key]);
        }
      }
    }
  };
  traverseAndRemove(planStore.content);
};

const saveDocument = async () => {
  formModal.processing = true;
  errors.value = {
    name: null,
    type: null,
    document: null,
  };

  const { data, context } = formModal;

  try {
    const formData = new FormData();
    formData.append('annexureType', data.type);

    if (!data.type) {
      errors.value.type = 'This is a required field';
    }

    if (data.file && data.file.rawFile) {
      formData.append('document', data.file.rawFile);
    } else if (data.file instanceof File) {
      formData.append('document', data.file);
    } else if (!context.isEditing) {
      errors.value.document = 'Document must be attached';
      formModal.processing = false;
      return;
    }

    // Check if the selected type is NOT required based on store data
    const selectedAnnexureType = planStore.annexureTypes.find(annexureType => annexureType.id === data.type);
    const isAdditional = selectedAnnexureType ? !selectedAnnexureType.required : false;

    if (isAdditional) {
      if (!data.name) {
        errors.value.name = 'This is a required field';
        formModal.processing = false;
        return;
      }
      formData.append('name', data.name);
    }

    let response;

    if (context.isEditing) {
      response = await useHttp().put(`/municipalities/${route.params.municipality}/plan/annexures/${data.id}`, formData);
    } else {
      response = await useHttp().post(`/municipalities/${route.params.municipality}/plan/annexures`, formData);
    }

    if (response.statusCode === HttpStatusCode.Ok) {
      const savedAnnexure = response.data;

      // If the annexure type is required, the backend will delete any existing annexure of that type.
      // We must mirror this state update on the frontend to avoid stale data.
      if (savedAnnexure.type?.required) {
        // Identify replaced annexures (Same Type ID, Different Annexure ID)
        const replacedAnnexures = planStore.planAnnexures.filter(annexure =>
          annexure.type?.id === savedAnnexure.type?.id && annexure.id !== savedAnnexure.id,
        );

        // Clean up references for replaced annexures from content sections
        replacedAnnexures.forEach(oldAnnexure => {
          cleanupGlobalContentReferences(oldAnnexure.id);
        });

        // Filter the store list to remove replaced items
        planStore.planAnnexures = planStore.planAnnexures.filter(annexure =>
          annexure.type?.id !== savedAnnexure.type?.id || annexure.id === savedAnnexure.id,
        );
      }

      // Update the Global Store List
      const existingIndex = planStore.planAnnexures.findIndex(a => a.id === savedAnnexure.id);
      if (existingIndex !== -1) {
        planStore.planAnnexures[existingIndex] = savedAnnexure;
      } else {
        planStore.planAnnexures.push(savedAnnexure);
      }

      // If we are in a content section context (storeKey exists), add the new ID to the array
      if (props.storeKey && !context.isEditing) {
        const pathParts = props.storeKey.split('.');
        const targetKey = pathParts.pop();
        const targetObj = pathParts.reduce((o, i) => o[i], planStore.content);

        if (targetObj) {
          if (!Array.isArray(targetObj[targetKey])) {
            targetObj[targetKey] = [];
          }
          // Avoid duplicates
          if (!targetObj[targetKey].includes(savedAnnexure.id)) {
            targetObj[targetKey].push(savedAnnexure.id);
          }
        }
      }

      formModal.close();
      errors.value = {
        name: null,
        type: null,
        document: null,
      };

      emit('saved');
    } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
      formModal.processing = false;
      toasts.error('Upload failed: File exceeds the 100MB limit.');
    }
  } catch (e) {
    console.error(e);
    toasts.error('An error occurred');
  }

  formModal.processing = false;
};

const deleteDocument = async () => {
  deleteModal.processing = true;
  const { data } = deleteModal;

  try {
    const response = await useHttp().delete(`/municipalities/${route.params.municipality}/plan/annexures/${data.id}`);

    if (response.statusCode === HttpStatusCode.Ok) {
      // Remove from store list
      const index = planStore.planAnnexures.findIndex(annexure => annexure.id === data.id);
      if (index !== -1) planStore.planAnnexures.splice(index, 1);

      // Remove ID from the content section if applicable
      if (props.storeKey) {
        removeDocumentIdFromContent(data.id);
      }

      emit('deleted');
      deleteModal.close();

      // Run the heavy cleanup in the next tick to prevent UI freeze
      setTimeout(() => {
        cleanupGlobalContentReferences(data.id);
      }, 50);
    }
  } catch (e) {
    console.error(e);
    toasts.error('An error occurred');
  }
  deleteModal.processing = false;
};

const handleCancel = () => {
  errors.value = {
    name: null,
    type: null,
    document: null,
  };

  formModal.close();
};
</script>
